import axios from "axios";
axios.defaults.withCredentials = true;
axios.defaults.timeout = 30000;

const apiKey = process.env.REACT_APP_X_API_KEY;
const appKey = process.env.REACT_APP_APP_KEY;
const appBaseUrl = process.env.REACT_APP_API_ADMIN_BASE_URL;
const transactionBaseUrl = process.env.REACT_APP_API_TRANSACTION_BASE_URL || 'http://localhost:8000/api/transactions';

// Helper function to check if user is test-partner (read-only)
const isTestPartner = () => {
    const userRole = localStorage.getItem("userRole");
    return userRole === "test-partner";
};

// Helper function to check write permission
const checkWritePermission = () => {
    if (isTestPartner()) {
        return {
            allowed: false,
            error: {
                status: "error",
                message: "Test accounts have read-only access. Write operations are not permitted.",
                error: "READ_ONLY_ACCESS"
            }
        };
    }
    return { allowed: true };
};

// Helper function to handle API errors including 403 for read-only access
const handleApiError = (error) => {
    if (error.response?.status === 403) {
        const errorData = error.response?.data;
        if (errorData?.error === "READ_ONLY_ACCESS" || errorData?.message?.includes("read-only")) {
            return {
                success: false,
                status: "error",
                message: errorData.message || "Test accounts have read-only access. Write operations are not permitted.",
                error: "READ_ONLY_ACCESS"
            };
        }
    }
    return {
        success: false,
        status: "error",
        message: error.response?.data?.message || error.response?.data?.error || "Something went wrong. Please try again.",
    };
};

const getRateLimitInfo = (error) => {
    const statusCode = error.response?.status;
    const message = error.response?.data?.message || error.response?.data?.error;
    const headerRetryAfter = error.response?.headers?.["retry-after"];
    const bodyRetryAfter = error.response?.data?.retry_after || error.response?.data?.retryAfter || error.response?.data?.data?.retry_after;
    const retryAfterSeconds = parseInt(headerRetryAfter || bodyRetryAfter, 10);
    const inferredRateLimit = typeof message === "string" && /rate limit|too many requests|too many attempts/i.test(message);
    const isRateLimited = statusCode === 429 || inferredRateLimit;

    return {
        isRateLimited,
        statusCode,
        message,
        retryAfterSeconds: Number.isFinite(retryAfterSeconds) ? retryAfterSeconds : null,
    };
};

// Helper function to get authentication headers based on user type
const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    const userType = localStorage.getItem("userType");
    const currentPartnerId = localStorage.getItem("pid");
    
    // If user has a Bearer token, use it
    if (token) {
        const headers = {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
        };
        
        // Also include partner ID if available
        if (currentPartnerId) {
            headers["X-Partner-ID"] = currentPartnerId;
        }
        
        return headers;
    }
    
    // If user is a partner and has app key, use partner authentication
    if (userType === "partner" && appKey && currentPartnerId) {
        return {
            "APP-KEY": appKey,
            "X-Partner-ID": currentPartnerId,
            "Content-Type": "application/json"
        };
    }
    
    // Fallback to X-API-KEY for legacy support
    return {
        "X-API-KEY": apiKey,
        "Content-Type": "application/json"
    };
};

// Helper function to get authentication headers specifically for refunds API
const getRefundsAuthHeaders = () => {
    const token = localStorage.getItem("token");
    const userType = localStorage.getItem("userType");
    const currentPartnerId = localStorage.getItem("pid");
    
    
    // Always include partner-id for refunds API
    const headers = {
        "Content-Type": "application/json"
    };
    
    // Add partner-id header
    if (currentPartnerId) {
        headers["partner-id"] = currentPartnerId;
    }
    
    // If user has a Bearer token, use it
    if (token) {
        headers["Authorization"] = `Bearer ${token}`;
        return headers;
    }
    
    // If user is a partner and has app key, use partner authen tication
    if (userType === "partner" && appKey && currentPartnerId) {
        headers["APP-KEY"] = appKey;
        return headers;
    }
    
    // Fallback to X-API-KEY for legacy support
    if (apiKey) {
        headers["X-API-KEY"] = apiKey;
    }
    
    return headers;
};

// Helper function to determine which API endpoint to use based on user type
export const getApiEndpoint = (userId) => {
    const userRole = localStorage.getItem("userRole");
    const userType = localStorage.getItem("userType");
    
    // If user is a partner or test-partner, use partner endpoints
    if (userType === "partner" || userRole === "partner" || userRole === "test-partner") {
        return `${appBaseUrl}/partner/${userId}`;
    }
    
    // Otherwise, use user endpoints (for support, admin, regular users)
    return `${appBaseUrl}/user/${userId}`;
};

export const registerPartner = async (formData) => {
    if (!apiKey || !appBaseUrl) {
        return { success: false, message: "Missing configuration. Please try again later." };
    }

    try {
        const response = await axios.post(
            `${appBaseUrl}/register-partner`,
            formData,
            {
                headers: {
                    "Content-Type": "application/json",
                    "X-API-KEY": apiKey,
                },
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        return {
            success: false,
            message: error.response?.data?.message || "Something went wrong. Please try again.",
        };
    }
};

export const login = async (formData) => {
    try {
        const response = await axios.post(
            `${appBaseUrl}/login`,
            formData,
            {
                headers: {
                    "Content-Type": "application/json"
                },
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        const rateLimitInfo = getRateLimitInfo(error);
        if (rateLimitInfo.isRateLimited) {
            return {
                success: false,
                status: "rate_limited",
                message: rateLimitInfo.message || "Too many login attempts. Please try again later.",
                retryAfterSeconds: rateLimitInfo.retryAfterSeconds,
                statusCode: rateLimitInfo.statusCode
            };
        }

        return {
            success: false,
            message: error.response?.data?.message || "Something went wrong. Please try again.",
            statusCode: error.response?.status
        };
    }
};

export const getPartner = async (partnerId) => {
    if (!appBaseUrl || !partnerId) {
        return { success: false, message: "Missing configuration or partner ID. Please try again later." };
    }

    try {
        const response = await axios.get(
            `${appBaseUrl}/partner/${partnerId}`,
            {
                headers: getAuthHeaders(),
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        return {
            success: false,
            message: error.response?.data?.message || "Something went wrong. Please try again.",
        };
    }
};

export const getUser = async (userId) => {
    if (!appBaseUrl || !userId) {
        return { success: false, message: "Missing configuration or user ID. Please try again later." };
    }

    try {
        const response = await axios.get(
            `${appBaseUrl}/user/${userId}`,
            {
                headers: getAuthHeaders(),
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        return {
            success: false,
            message: error.response?.data?.message || "Something went wrong. Please try again.",
        };
    }
};

export const updatePartner = async (partnerId, updatedData) => {
    if (!appBaseUrl || !partnerId) {
        return { success: false, message: "Missing configuration or partner ID. Please try again later." };
    }

    const permissionCheck = checkWritePermission();
    if (!permissionCheck.allowed) {
        return permissionCheck.error;
    }

    try {
        const response = await axios.post(
            `${appBaseUrl}/partner/${partnerId}`,
            updatedData,
            {
                headers: getAuthHeaders(),
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        return handleApiError(error);
    }
};

export const updateUser = async (userId, updatedData) => {
    if (!appBaseUrl || !userId) {
        return { success: false, message: "Missing configuration or user ID. Please try again later." };
    }

    try {
        const response = await axios.post(
            `${appBaseUrl}/user/${userId}`,
            updatedData,
            {
                headers: getAuthHeaders(),
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        return {
            success: false,
            message: error.response?.data?.message || "Something went wrong. Please try again.",
        };
    }
};

export const getOutflows = async (partnerId, page = 1, perPage = 10) => {
    if (!appBaseUrl || !partnerId) {
        return { success: false, message: "Missing configuration or partner ID. Please try again later." };
    }

    try {
        const response = await axios.get(
            `${appBaseUrl}/partner/${partnerId}/outflows`,
            {
                headers: getAuthHeaders(),
                params: {
                    page,
                    per_page: perPage,
                },
                withCredentials: true,
            }
        );

        // Check if the requested page is valid
        const totalCount = response.data.data.total_outflows || 0;
        const totalPages = Math.ceil(totalCount / perPage);
        const requestedPage = parseInt(page, 10);
        
        if (requestedPage > totalPages && totalPages > 0) {
            // Return corrected response for page 1
            return {
                success: response.data.status === "success",
                outflows: response.data.data.outflows || [],
                total: totalCount,
                pagination: {
                    current_page: 1,
                    per_page: perPage,
                    total: totalCount,
                    last_page: totalPages
                },
                corrected: true
            };
        }

        return {
            success: response.data.status === "success",
            outflows: response.data.data.outflows || [],
            total: totalCount,
            pagination: response.data.data.pagination || {
                current_page: page,
                per_page: perPage,
                total: totalCount,
                last_page: totalPages
            },
        };
    } catch (error) {
        return {
            success: false,
            message: error.response?.data?.message || "Something went wrong. Please try again.",
        };
    }
};

export const getInflows = async (partnerId, page = 1, perPage = 10) => {
    if (!appBaseUrl || !partnerId) {
        return { success: false, message: "Missing configuration or partner ID. Please try again later." };
    }

    try {
        const response = await axios.get(
            `${appBaseUrl}/partner/${partnerId}/inflows`,
            {
                headers: getAuthHeaders(),
                params: {
                    page,
                    per_page: perPage,
                },
                withCredentials: true,
            }
        );

        // Check if the requested page is valid
        const totalCount = response.data.data.total_inflows || 0;
        const totalPages = Math.ceil(totalCount / perPage);
        const requestedPage = parseInt(page, 10);
        
        if (requestedPage > totalPages && totalPages > 0) {
            // Return corrected response for page 1
            return {
                success: response.data.status === "success",
                inflows: response.data.data.inflows || [],
                total: totalCount,
                pagination: {
                    current_page: 1,
                    per_page: perPage,
                    total: totalCount,
                    last_page: totalPages
                },
                corrected: true
            };
        }

        return {
            success: response.data.status === "success",
            inflows: response.data.data.inflows || [],
            total: totalCount,
            pagination: response.data.data.pagination || {
                current_page: page,
                per_page: perPage,
                total: totalCount,
                last_page: totalPages
            },
        };
    } catch (error) {
        return {
            success: false,
            message: error.response?.data?.message || "Something went wrong. Please try again.",
        };
    }
};

// PAYIN API

export const getPayInStats = async (partnerId, sync = false, page = 1, perPage = 10) => {
    if (!appBaseUrl || !partnerId) {
        return { success: false, message: "Missing configuration or partner ID. Please try again later." };
    }

    try {
        const response = await axios.get(
            `${appBaseUrl}/payin/stats/${sync}`,
            {
                headers: getAuthHeaders(),
                params: {
                    page,
                    per_page: perPage,
                },
                withCredentials: true,
            }
        );

        return {
            success: response.data.status === "success",
            total_inflows: response.data.data.total_inflows,
            total_outflows: response.data.data.total_outflows,
            account_balance: response.data.data.account_balance,
            inflows_count: response.data.data.inflows_count,
            outflows_count: response.data.data.outflows_count,
            synced_transactions: response.data.data.synced_transactions,
            pagination: response.data.data.pagination,
        };
    } catch (error) {
        return {
            success: false,
            message: error.response?.data?.message || "Something went wrong. Please try again.",
        };
    }
};

export const getPayInInflows = async (partnerId, page = 1, perPage = 10) => {
    if (!appBaseUrl || !partnerId) {
        return { success: false, message: "Missing configuration or partner ID. Please try again later." };
    }

    try {
        const response = await axios.get(
            `${appBaseUrl}/payin/inflows`,
            {
                headers: getAuthHeaders(),
                params: {
                    page,
                    per_page: perPage,
                },
                withCredentials: true,
            }
        );
        
        // Check if the requested page is valid
        const totalCount = response.data.data.total_inflows || 0;
        const totalPages = Math.ceil(totalCount / perPage);
        const requestedPage = parseInt(page, 10);
        
        if (requestedPage > totalPages && totalPages > 0) {
            // Return corrected response for page 1
            return {
                success: response.data.status === "success",
                inflows: response.data.data.inflows || [],
                total: totalCount,
                pagination: {
                    current_page: 1,
                    per_page: perPage,
                    total: totalCount,
                    last_page: totalPages
                },
                corrected: true
            };
        }

        
        return {
            success: response.data.status === "success",
            inflows: response.data.data.inflows || [],
            total: totalCount,
            pagination: response.data.data.pagination || {
                current_page: page,
                per_page: perPage,
                total: totalCount,
                last_page: totalPages
            },
        };
    } catch (error) {
        return {
            success: false,
            message: error.response?.data?.message || "Something went wrong. Please try again.",
        };
    }
};

export const getPayInOutflows = async (partnerId, page = 1, perPage = 10) => {
    if (!appBaseUrl || !partnerId) {
        return { success: false, message: "Missing configuration or partner ID. Please try again later." };
    }

    try {
        const response = await axios.get(
            `${appBaseUrl}/payin/outflows`,
            {
                headers: getAuthHeaders(),
                params: {
                    page,
                    per_page: perPage,
                },
                withCredentials: true,
            }
        );
        
        // Check if the requested page is valid
        const totalCount = response.data.data.total_outflows || 0;
        const totalPages = Math.ceil(totalCount / perPage);
        const requestedPage = parseInt(page, 10);
        
        if (requestedPage > totalPages && totalPages > 0) {
            // Return corrected response for page 1
            return {
                success: response.data.status === "success",
                outflows: response.data.data.outflows || [],
                total: totalCount,
                pagination: {
                    current_page: 1,
                    per_page: perPage,
                    total: totalCount,
                    last_page: totalPages
                },
                corrected: true
            };
        }
        
        return {
            success: response.data.status === "success",
            outflows: response.data.data.outflows || [],
            total: totalCount,
            pagination: response.data.data.pagination || {
                current_page: page,
                per_page: perPage,
                total: totalCount,
                last_page: totalPages
            },
        };
    } catch (error) {
        return {
            success: false,
            message: error.response?.data?.message || "Something went wrong. Please try again.",
        };
    }
};

export const transferToPayout = async (partnerId, transferData) => {
    if (!appBaseUrl || !partnerId) {
        return { success: false, message: "Missing configuration or partner ID. Please try again later." };
    }

    const permissionCheck = checkWritePermission();
    if (!permissionCheck.allowed) {
        return {
            success: false,
            ...permissionCheck.error
        };
    }

    try {
        const response = await axios.post(
            `${appBaseUrl}/payin/transfer-to-payout`,
            transferData,
            {
                headers: getAuthHeaders(),
                withCredentials: true,
            }
        );
        
        return {
            success: response.data.status === "success",
            message: response.data.message,
            data: response.data.data,
            newPayinBalance: response.data.data?.new_payin_balance,
            newPayoutBalance: response.data.data?.new_payout_balance,
            transactionReference: response.data.data?.transaction_reference,
        };
    } catch (error) {
        const errorResponse = handleApiError(error);
        return {
            success: false,
            message: errorResponse.message,
            error: errorResponse.error || error.message,
        };
    }
};

export const getTransferToPayout = async (partnerId, page = 1, perPage = 10, filters = {}) => {
    if (!appBaseUrl || !partnerId) {
        return { success: false, message: "Missing configuration or partner ID. Please try again later." };
    }

    try {
        // Clean filters to remove empty values
        const cleanFilters = { ...filters };
        Object.keys(cleanFilters).forEach(key => {
            if (cleanFilters[key] === '' || cleanFilters[key] === null || cleanFilters[key] === undefined) {
                delete cleanFilters[key];
            }
        });

        const params = new URLSearchParams({
            page: page.toString(),
            per_page: perPage.toString(),
            ...cleanFilters
        });

        const response = await axios.get(
            `${appBaseUrl}/payin/transfer-to-payout?${params}`,
            {
                headers: getAuthHeaders(),
                withCredentials: true,
            }
        );

        if (response.data.status === "success") {
            const transfers = response.data.data.transfers || [];
            const totalCount = response.data.data.total_count || 0;
            const totalPages = Math.ceil(totalCount / perPage);
            const requestedPage = parseInt(page, 10);
            
            if (requestedPage > totalPages && totalPages > 0) {
                return {
                    success: true,
                    transfers: [],
                    total: totalCount,
                    pagination: {
                        current_page: 1,
                        per_page: perPage,
                        total: totalCount,
                        last_page: totalPages
                    },
                    corrected: true
                };
            }
            
            return {
                success: true,
                transfers: transfers,
                total: totalCount,
                pagination: response.data.data.pagination || {
                    current_page: page,
                    per_page: perPage,
                    total: totalCount,
                    last_page: totalPages
                },
            };
        } else {
            return {
                success: false,
                message: response.data.message || "Failed to fetch transfer to payout data",
            };
        }
    } catch (error) {
        return {
            success: false,
            message: error.response?.data?.message || "Something went wrong. Please try again.",
        };
    }
};

// EXPORT API FUNCTIONS

export const exportPayInInflows = async (partnerId, filters = {}) => {
  if (!appBaseUrl || !partnerId) {
    return { success: false, message: "Missing configuration or partner ID. Please try again later." };
  }

  try {
    const params = new URLSearchParams(filters);
    const authHeaders = getAuthHeaders();
    const response = await fetch(`${appBaseUrl}/payin/inflows/export?${params}`, {
      method: 'GET',
      headers: authHeaders,
    });

    if (response.ok) {
      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition?.split('filename=')[1]?.replace(/"/g, '') || `payin-inflows-${Date.now()}.xlsx`;
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      
      return { success: true, message: "Export completed successfully" };
    } else {
      const errorData = await response.json();
      return { 
        success: false, 
        message: errorData.message || "Export failed. Please try again." 
      };
    }
  } catch (error) {
    return { 
      success: false, 
      message: "Export failed. Please try again." 
    };
  }
};

export const exportPayInOutflows = async (partnerId, filters = {}) => {
  if (!appBaseUrl || !partnerId) {
    return { success: false, message: "Missing configuration or partner ID. Please try again later." };
  }

  try {
    const params = new URLSearchParams(filters);
    const authHeaders = getAuthHeaders();
    const response = await fetch(`${appBaseUrl}/payin/outflows/export?${params}`, {
      method: 'GET',
      headers: authHeaders,
    });

    if (response.ok) {
      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition?.split('filename=')[1]?.replace(/"/g, '') || `payin-outflows-${Date.now()}.xlsx`;
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      
      return { success: true, message: "Export completed successfully" };
    } else {
      const errorData = await response.json();
      return { 
        success: false, 
        message: errorData.message || "Export failed. Please try again." 
      };
    }
  } catch (error) {
    return { 
      success: false, 
      message: "Export failed. Please try again." 
    };
  }
};

export const exportPartnerInflows = async (partnerId, filters = {}) => {
  if (!appBaseUrl || !partnerId) {
    return { success: false, message: "Missing configuration or partner ID. Please try again later." };
  }

  try {
    const params = new URLSearchParams(filters);
    const authHeaders = getAuthHeaders();
    const response = await fetch(`${appBaseUrl}/partner/${partnerId}/inflows/export?${params}`, {
      method: 'GET',
      headers: authHeaders,
    });

    if (response.ok) {
      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition?.split('filename=')[1]?.replace(/"/g, '') || `partner-${partnerId}-inflows-${Date.now()}.xlsx`;
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      
      return { success: true, message: "Export completed successfully" };
    } else {
      const errorData = await response.json();
      return { 
        success: false, 
        message: errorData.message || "Export failed. Please try again." 
      };
    }
  } catch (error) {
    return { 
      success: false, 
      message: "Export failed. Please try again." 
    };
  }
};

export const exportPartnerOutflows = async (partnerId, filters = {}) => {
  if (!appBaseUrl || !partnerId) {
    return { success: false, message: "Missing configuration or partner ID. Please try again later." };
  }

  try {
    const params = new URLSearchParams(filters);
    const authHeaders = getAuthHeaders();
    const response = await fetch(`${appBaseUrl}/partner/${partnerId}/outflows/export?${params}`, {
      method: 'GET',
      headers: authHeaders,
    });

    if (response.ok) {
      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition?.split('filename=')[1]?.replace(/"/g, '') || `partner-${partnerId}-outflows-${Date.now()}.xlsx`;
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      
      return { success: true, message: "Export completed successfully" };
    } else {
      const errorData = await response.json();
      return { 
        success: false, 
        message: errorData.message || "Export failed. Please try again." 
      };
    }
  } catch (error) {
    return { 
      success: false, 
      message: "Export failed. Please try again." 
    };
  }
};

export const exportTransferToPayout = async (partnerId, filters = {}) => {
  if (!appBaseUrl || !partnerId) {
    return { success: false, message: "Missing configuration or partner ID. Please try again later." };
  }

  try {
    // Clean filters to remove empty values
    const cleanFilters = { ...filters };
    Object.keys(cleanFilters).forEach(key => {
      if (cleanFilters[key] === '' || cleanFilters[key] === null || cleanFilters[key] === undefined) {
        delete cleanFilters[key];
      }
    });

    const params = new URLSearchParams(cleanFilters);
    const authHeaders = getAuthHeaders();
    const response = await fetch(`${appBaseUrl}/payin/transfer-to-payout/export?${params}`, {
      method: 'GET',
      headers: authHeaders,
    });

    if (response.ok) {
      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition?.split('filename=')[1]?.replace(/"/g, '') || `transfer-to-payout-${Date.now()}.xlsx`;
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      
      return { success: true, message: "Export completed successfully" };
    } else {
      const errorData = await response.json();
      return { 
        success: false, 
        message: errorData.message || "Export failed. Please try again." 
      };
    }
  } catch (error) {
    return { 
      success: false, 
      message: "Export failed. Please try again." 
    };
  }
};

// ADDITIONAL ENDPOINTS FROM BACKEND REFERENCE

// Logout
export const logout = async () => {
    if (!appBaseUrl) {
        return { success: false, message: "Missing configuration. Please try again later." };
    }

    try {
        const response = await axios.post(
            `${appBaseUrl}/logout`,
            {},
            {
                headers: getAuthHeaders(),
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        return {
            success: false,
            message: error.response?.data?.message || "Something went wrong. Please try again.",
        };
    }
};

// Logout All Devices
export const logoutAll = async () => {
    if (!appBaseUrl) {
        return { success: false, message: "Missing configuration. Please try again later." };
    }

    try {
        const response = await axios.post(
            `${appBaseUrl}/logout-all`,
            {},
            {
                headers: getAuthHeaders(),
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        return {
            success: false,
            message: error.response?.data?.message || "Something went wrong. Please try again.",
        };
    }
};

// Get Current User
export const getCurrentUser = async () => {
    if (!appBaseUrl) {
        return { success: false, message: "Missing configuration. Please try again later." };
    }

    try {
        const response = await axios.get(
            `${appBaseUrl}/me`,
            {
                headers: getAuthHeaders(),
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        return {
            success: false,
            message: error.response?.data?.message || "Something went wrong. Please try again.",
        };
    }
};

// Analytics Dashboard
export const getAnalyticsDashboard = async () => {
    if (!appBaseUrl) {
        return { success: false, message: "Missing configuration. Please try again later." };
    }

    try {
        const response = await axios.get(
            `${appBaseUrl}/analytics/dashboard`,
            {
                headers: getAuthHeaders(),
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        return {
            success: false,
            message: error.response?.data?.message || "Something went wrong. Please try again.",
        };
    }
};

// Partner Analytics
export const getPartnerAnalytics = async (partnerId) => {
    if (!appBaseUrl || !partnerId) {
        return { success: false, message: "Missing configuration or partner ID. Please try again later." };
    }

    try {
        const response = await axios.get(
            `${appBaseUrl}/analytics/partner/${partnerId}`,
            {
                headers: getAuthHeaders(),
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        return {
            success: false,
            message: error.response?.data?.message || "Something went wrong. Please try again.",
        };
    }
};

// Transaction Analytics
export const getTransactionAnalytics = async () => {
    if (!appBaseUrl) {
        return { success: false, message: "Missing configuration. Please try again later." };
    }

    try {
        const response = await axios.get(
            `${appBaseUrl}/analytics/transactions`,
            {
                headers: getAuthHeaders(),
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        return {
            success: false,
            message: error.response?.data?.message || "Something went wrong. Please try again.",
        };
    }
};

// Get All Banks
export const getAllBanks = async () => {
    if (!appBaseUrl) {
        return { success: false, message: "Missing configuration. Please try again later." };
    }

    try {
        const response = await axios.get(
            `${appBaseUrl}/transactions/get-all-banks`,
            {
                headers: getAuthHeaders(),
                withCredentials: true,
            }
        );

        return { success: true, data: response.data };
    } catch (error) {
        return {
            success: false,
            message: error.response?.data?.message || "Something went wrong. Please try again.",
        };
    }
};

// Get Bank Name (Account Verification)
export const getBankName = async (bankCode, accountNumber) => {
    if (!appBaseUrl || !bankCode || !accountNumber) {
        return { success: false, message: "Missing configuration, bank code, or account number. Please try again later." };
    }

    try {
        const response = await axios.get(
            `${appBaseUrl}/transactions/get-bank-name`,
            {
                params: {
                    bank_code: bankCode,
                    acct_no: accountNumber
                },
                headers: getAuthHeaders(),
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        return {
            success: false,
            message: error.response?.data?.message || error.response?.data?.error || "Account verification failed. Please check your account number and try again.",
        };
    }
};

// Transfer Funds
export const transferFunds = async (transferData) => {
    if (!appBaseUrl) {
        return { success: false, message: "Missing configuration. Please try again later." };
    }

    const permissionCheck = checkWritePermission();
    if (!permissionCheck.allowed) {
        return permissionCheck.error;
    }

    try {
        const response = await axios.post(
            `${appBaseUrl}/transactions/transfer-funds`,
            transferData,
            {
                headers: getAuthHeaders(),
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        return handleApiError(error);
    }
};

// Settlement Status
export const getSettlementStatus = async (transactionId) => {
    if (!appBaseUrl || !transactionId) {
        return { success: false, message: "Missing configuration or transaction ID. Please try again later." };
    }

    try {
        const response = await axios.post(
            `${appBaseUrl}/transactions/settlement-status`,
            { transactionId },
            {
                headers: getAuthHeaders(),
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        return {
            success: false,
            message: error.response?.data?.message || "Something went wrong. Please try again.",
        };
    }
};

// Settlement Status from DB
export const getSettlementStatusFromDB = async (transactionId) => {
    if (!appBaseUrl || !transactionId) {
        return { success: false, message: "Missing configuration or transaction ID. Please try again later." };
    }

    try {
        const response = await axios.post(
            `${appBaseUrl}/transactions/settlement-status-db`,
            { transactionId },
            {
                headers: getAuthHeaders(),
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        return {
            success: false,
            message: error.response?.data?.message || "Something went wrong. Please try again.",
        };
    }
};

// Update Partner App Key
export const updatePartnerAppKey = async (partnerId, newAppKey) => {
    if (!appBaseUrl || !partnerId || !newAppKey) {
        return { success: false, message: "Missing configuration, partner ID, or new app key. Please try again later." };
    }

    const permissionCheck = checkWritePermission();
    if (!permissionCheck.allowed) {
        return permissionCheck.error;
    }

    try {
        const response = await axios.post(
            `${appBaseUrl}/partner/${partnerId}/update-app-key`,
            { newAppKey },
            {
                headers: getAuthHeaders(),
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        return {
            success: false,
            message: error.response?.data?.message || "Something went wrong. Please try again.",
        };
    }
};

// Get All Partners
export const getAllPartners = async (page = 1, perPage = 100, search = null) => {
    if (!appBaseUrl) {
        return { success: false, message: "Missing configuration. Please try again later." };
    }

    try {
        const params = { page, per_page: perPage };
        if (search) params.search = search;

        const response = await axios.get(
            `${appBaseUrl}/partners`,
            {
                params,
                headers: getAuthHeaders(),
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        return {
            success: false,
            message: error.response?.data?.message || "Something went wrong. Please try again.",
        };
    }
};

// Create Partner
export const createPartner = async (partnerData) => {
    if (!appBaseUrl) {
        return { success: false, message: "Missing configuration. Please try again later." };
    }

    try {
        const response = await axios.post(
            `${appBaseUrl}/partners`,
            partnerData,
            {
                headers: getAuthHeaders(),
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        return {
            success: false,
            message: error.response?.data?.message || "Something went wrong. Please try again.",
        };
    }
};

// Get Specific Partner
export const getSpecificPartner = async (partnerId) => {
    if (!appBaseUrl || !partnerId) {
        return { success: false, message: "Missing configuration or partner ID. Please try again later." };
    }

    try {
        const response = await axios.get(
            `${appBaseUrl}/partners/${partnerId}`,
            {
                headers: getAuthHeaders(),
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        return {
            success: false,
            message: error.response?.data?.message || "Something went wrong. Please try again.",
        };
    }
};

// Delete Partner
export const deletePartner = async (partnerId) => {
    if (!appBaseUrl || !partnerId) {
        return { success: false, message: "Missing configuration or partner ID. Please try again later." };
    }

    try {
        const response = await axios.post(
            `${appBaseUrl}/partners/${partnerId}/delete`,
            {},
            {
                headers: getAuthHeaders(),
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        return {
            success: false,
            message: error.response?.data?.message || "Something went wrong. Please try again.",
        };
    }
};

// RENDERED TRANSACTIONS API FUNCTIONS

// Get Rendered Transactions
export const getRenderedTransactions = async (partnerId) => {
    if (!transactionBaseUrl) {
        return { success: false, message: "Missing configuration. Please try again later." };
    }

    try {
        const response = await axios.get(
            `${transactionBaseUrl}/rendered`,
            {
                params: { partnerId },
                headers: getAuthHeaders(),
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        return {
            success: false,
            message: error.response?.data?.message || error.response?.data?.error || "Failed to fetch rendered transactions. Please try again.",
            statusCode: error.response?.status,
            isNotFound: error.response?.status === 404
        };
    }
};

// REFUNDS API FUNCTIONS

// Process Refund
export const processRefund = async (refundData) => {
    if (!transactionBaseUrl) {
        return { success: false, message: "Missing configuration. Please try again later." };
    }

    const permissionCheck = checkWritePermission();
    if (!permissionCheck.allowed) {
        return {
            status: false,
            ...permissionCheck.error
        };
    }

    try {
        const response = await axios.post(
            `${transactionBaseUrl}/refunds/process-refund`,
            refundData,
            {
                headers: getRefundsAuthHeaders(),
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        const errorResponse = handleApiError(error);
        return {
            status: false,
            success: false,
            message: errorResponse.message,
            error: errorResponse.error,
        };
    }
};

// Get Successful Refunds
export const getSuccessfulRefunds = async (filters = {}) => {
    if (!transactionBaseUrl) {
        return { success: false, message: "Missing configuration. Please try again later." };
    }

    try {
        const params = new URLSearchParams();
        
        if (filters.start_date) params.append('start_date', filters.start_date);
        if (filters.end_date) params.append('end_date', filters.end_date);
        if (filters.refund_type) params.append('refund_type', filters.refund_type);
        if (filters.page) params.append('page', filters.page);

        const url = `${transactionBaseUrl}/refunds/successful?${params}`;
        const headers = getRefundsAuthHeaders();

        const response = await axios.get(url, {
            headers: headers,
            withCredentials: true,
        });

        return {
            success: response.data.status === true,
            data: response.data.data || { data: [], pagination: {} },
        };
    } catch (error) {
        return {
            success: false,
            message: error.response?.data?.message || error.response?.data?.error || `Failed to fetch successful refunds. Status: ${error.response?.status || 'Unknown'}`,
        };
    }
};

// Get Failed Refunds
export const getFailedRefunds = async (filters = {}) => {
    if (!transactionBaseUrl) {
        return { success: false, message: "Missing configuration. Please try again later." };
    }

    try {
        const params = new URLSearchParams();
        
        if (filters.start_date) params.append('start_date', filters.start_date);
        if (filters.end_date) params.append('end_date', filters.end_date);
        if (filters.status) params.append('status', filters.status);
        if (filters.page) params.append('page', filters.page);

        const response = await axios.get(
            `${transactionBaseUrl}/refunds/failed?${params}`,
            {
                headers: getRefundsAuthHeaders(),
                withCredentials: true,
            }
        );

        return {
            success: response.data.status === true,
            data: response.data.data || { data: [], pagination: {} },
        };
    } catch (error) {
        return {
            success: false,
            message: error.response?.data?.message || error.response?.data?.error || "Failed to fetch failed refunds.",
        };
    }
};

// Get Pending Refunds
export const getPendingRefunds = async (filters = {}) => {
    if (!transactionBaseUrl) {
        return { success: false, message: "Missing configuration. Please try again later." };
    }

    try {
        const params = new URLSearchParams();
        
        if (filters.status) params.append('status', filters.status);
        if (filters.page) params.append('page', filters.page);

        const response = await axios.get(
            `${transactionBaseUrl}/refunds/pending?${params}`,
            {
                headers: getRefundsAuthHeaders(),
                withCredentials: true,
            }
        );

        return {
            success: response.data.status === true,
            data: response.data.data || { data: [], pagination: {} },
        };
    } catch (error) {
        return {
            success: false,
            message: error.response?.data?.message || error.response?.data?.error || "Failed to fetch pending refunds.",
        };
    }
};

// Check Refund Settlement Status
export const checkRefundStatus = async (transactionRef) => {
    if (!transactionBaseUrl || !transactionRef) {
        return { success: false, message: "Missing configuration or transaction reference. Please try again later." };
    }

    try {
        const response = await axios.post(
            `${transactionBaseUrl}/refunds/settlement-status`,
            { transaction_ref: transactionRef },
            {
                headers: getRefundsAuthHeaders(),
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        return {
            success: false,
            message: error.response?.data?.message || error.response?.data?.error || "Failed to check refund status.",
        };
    }
};

// Export Successful Refunds
export const exportSuccessfulRefunds = async (filters = {}) => {
    if (!transactionBaseUrl) {
        return { success: false, message: "Missing configuration. Please try again later." };
    }

    try {
        const params = new URLSearchParams();
        
        if (filters.start_date) params.append('start_date', filters.start_date);
        if (filters.end_date) params.append('end_date', filters.end_date);

        const authHeaders = getRefundsAuthHeaders();
        const response = await fetch(`${transactionBaseUrl}/refunds/successful/export?${params}`, {
            method: 'GET',
            headers: authHeaders,
        });

        if (response.ok) {
            const blob = await response.blob();
            const contentDisposition = response.headers.get('Content-Disposition');
            const filename = contentDisposition?.split('filename=')[1]?.replace(/"/g, '') || `successful-refunds-${new Date().toISOString().split('T')[0]}.csv`;
            
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            
            return { success: true, message: "Export completed successfully" };
        } else {
            const errorData = await response.json();
            return { 
                success: false, 
                message: errorData.message || errorData.error || "Export failed. Please try again." 
            };
        }
    } catch (error) {
        return { 
            success: false, 
            message: "Export failed. Please try again." 
        };
    }
};

// Export Failed Refunds
export const exportFailedRefunds = async (filters = {}) => {
    if (!transactionBaseUrl) {
        return { success: false, message: "Missing configuration. Please try again later." };
    }

    try {
        const params = new URLSearchParams();
        
        if (filters.start_date) params.append('start_date', filters.start_date);
        if (filters.end_date) params.append('end_date', filters.end_date);

        const authHeaders = getRefundsAuthHeaders();
        const response = await fetch(`${transactionBaseUrl}/refunds/failed/export?${params}`, {
            method: 'GET',
            headers: authHeaders,
        });

        if (response.ok) {
            const blob = await response.blob();
            const contentDisposition = response.headers.get('Content-Disposition');
            const filename = contentDisposition?.split('filename=')[1]?.replace(/"/g, '') || `failed-refunds-${new Date().toISOString().split('T')[0]}.csv`;
            
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            
            return { success: true, message: "Export completed successfully" };
        } else {
            const errorData = await response.json();
            return { 
                success: false, 
                message: errorData.message || errorData.error || "Export failed. Please try again." 
            };
        }
    } catch (error) {
        return { 
            success: false, 
            message: "Export failed. Please try again." 
        };
    }
};